import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn';

const ColorPicker = React.forwardRef(({
  className,
  label,
  description,
  error,
  required = false,
  value = '#3B82F6',
  onChange,
  disabled = false,
  id,
  showInput = true,
  showPreview = true,
  previewSize = 'md', // 'sm', 'md', 'lg'
  onPreviewClick,
  ...props
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });

  const triggerRef = useRef(null);
  const previewTriggerRef = useRef(null);
  const modalRef = useRef(null);

  // Predefined color palette for mobile app themes
  const colorPalette = [
    '#3B82F6', '#EF4444', '#10B981', '#F59E0B',
    '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16',
    '#F97316', '#6366F1', '#14B8A6', '#A855F7',
    '#DC2626', '#059669', '#7C3AED', '#DB2777',
    '#0891B2', '#65A30D', '#EA580C', '#4F46E5',
    '#0D9488', '#9333EA', '#1F2937', '#374151',
    '#FFFFFF', '#000000',
  ];

  const sizeConfigs = {
    sm: { preview: 'w-6 h-6', button: 'w-8 h-8' },
    md: { preview: 'w-10 h-10', button: 'w-10 h-10' },
    lg: { preview: 'w-12 h-12', button: 'w-12 h-12' }
  };

  const sizeConfig = sizeConfigs?.[previewSize] || sizeConfigs?.md;

  // Calculate modal position based on trigger element
  const calculatePosition = useCallback((triggerEl) => {
    if (!triggerEl) return;
    const rect = triggerEl?.getBoundingClientRect();
    const modalWidth = 280;
    const modalHeight = 320;
    const padding = 8;

    let top = rect?.bottom + padding;
    let left = rect?.left;

    // Prevent overflow on right edge
    if (left + modalWidth > window.innerWidth - padding) {
      left = window.innerWidth - modalWidth - padding;
    }
    // Prevent overflow on left edge
    if (left < padding) {
      left = padding;
    }
    // Flip above if not enough space below
    if (top + modalHeight > window.innerHeight - padding) {
      top = rect?.top - modalHeight - padding;
    }
    // Ensure not above viewport
    if (top < padding) {
      top = padding;
    }

    setModalPosition({ top, left });
  }, []);

  const openModal = useCallback((triggerEl, setter) => {
    calculatePosition(triggerEl);
    setter(true);
  }, [calculatePosition]);

  // Close on outside click or scroll
  useEffect(() => {
    if (!isOpen && !isPreviewOpen) return;

    const handleClose = (e) => {
      if (
        modalRef?.current && !modalRef?.current?.contains(e?.target) &&
        triggerRef?.current && !triggerRef?.current?.contains(e?.target) &&
        previewTriggerRef?.current && !previewTriggerRef?.current?.contains(e?.target)
      ) {
        setIsOpen(false);
        setIsPreviewOpen(false);
      }
    };

    const handleScroll = () => {
      setIsOpen(false);
      setIsPreviewOpen(false);
    };

    document.addEventListener('mousedown', handleClose);
    document.addEventListener('touchstart', handleClose);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleClose);
      document.removeEventListener('touchstart', handleClose);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, isPreviewOpen]);

  const handleColorSelect = (color) => {
    onChange?.(color);
    setIsOpen(false);
    setIsPreviewOpen(false);
  };

  const handleInputChange = (e) => {
    onChange?.(e?.target?.value);
  };

  const handlePreviewClick = () => {
    if (disabled) return;
    if (onPreviewClick) {
      onPreviewClick();
    } else {
      if (isPreviewOpen) {
        setIsPreviewOpen(false);
      } else {
        openModal(previewTriggerRef?.current, setIsPreviewOpen);
      }
    }
  };

  // Modal portal content
  const renderModal = () => {
    const isVisible = isOpen || isPreviewOpen;
    if (!isVisible) return null;

    return createPortal(
      <>
        {/* Backdrop for mobile tap-to-close */}
        <div
          className="fixed inset-0 z-[9998]"
          onClick={() => { setIsOpen(false); setIsPreviewOpen(false); }}
        />
        {/* Modal panel */}
        <div
          ref={modalRef}
          className="fixed z-[9999] bg-background border border-border rounded-xl shadow-2xl p-4"
          style={{
            top: modalPosition?.top,
            left: modalPosition?.left,
            width: 'min(280px, calc(100vw - 16px))',
          }}
          onClick={(e) => e?.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-foreground">Choose Color</span>
            <button
              type="button"
              onClick={() => { setIsOpen(false); setIsPreviewOpen(false); }}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors text-lg leading-none"
              aria-label="Close color picker"
            >
              ×
            </button>
          </div>

          {/* Color Grid */}
          <div className="grid grid-cols-6 gap-2 mb-4">
            {colorPalette?.map((color) => (
              <button
                key={color}
                type="button"
                onClick={() => handleColorSelect(color)}
                className={cn(
                  "w-9 h-9 rounded-lg border-2 transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
                  "hover:scale-110 active:scale-95",
                  value === color
                    ? "border-primary ring-2 ring-primary ring-offset-1 scale-110" :"border-transparent hover:border-border"
                )}
                style={{ backgroundColor: color }}
                aria-label={`Select color ${color}`}
                title={color}
              />
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-border pt-3">
            <label className="text-xs font-medium text-muted-foreground mb-2 block">
              Custom Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={value}
                onChange={(e) => onChange?.(e?.target?.value)}
                className="w-10 h-10 rounded-lg border border-input cursor-pointer flex-shrink-0 p-0.5 bg-background"
              />
              <input
                type="text"
                value={value}
                onChange={(e) => onChange?.(e?.target?.value)}
                placeholder="#3B82F6"
                className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          {/* Current color preview */}
          <div className="mt-3 flex items-center gap-2 p-2 bg-muted rounded-lg">
            <div
              className="w-6 h-6 rounded-md border border-border flex-shrink-0"
              style={{ backgroundColor: value }}
            />
            <span className="text-xs font-mono text-muted-foreground truncate">{value}</span>
          </div>
        </div>
      </>,
      document.body
    );
  };

  // Standalone preview component (no input shown)
  if (!showInput && showPreview) {
    return (
      <div className="relative inline-block">
        <button
          ref={previewTriggerRef}
          type="button"
          onClick={handlePreviewClick}
          disabled={disabled}
          className={cn(
            sizeConfig?.preview,
            "rounded-md border border-input shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer hover:scale-105 transition-transform",
            className
          )}
          style={{ backgroundColor: value }}
          aria-label="Open color picker"
          title={`Current color: ${value} - Click to change`}
        />
        {renderModal()}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {label && (
        <label
          htmlFor={id}
          className={cn(
            "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
            error ? "text-destructive" : "text-foreground"
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <div className="flex items-center space-x-2">
          {/* Color Preview Button */}
          {showPreview && (
            <button
              ref={triggerRef}
              type="button"
              onClick={() => {
                if (disabled) return;
                if (isOpen) {
                  setIsOpen(false);
                } else {
                  openModal(triggerRef?.current, setIsOpen);
                }
              }}
              disabled={disabled}
              className={cn(
                sizeConfig?.button,
                "rounded-md border border-input shadow-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer hover:scale-105 transition-transform flex-shrink-0",
                className
              )}
              style={{ backgroundColor: value }}
              aria-label="Open color picker"
              title={`Current color: ${value} - Click to change`}
            />
          )}

          {/* Color Input Field */}
          {showInput && (
            <input
              type="text"
              value={value}
              onChange={handleInputChange}
              disabled={disabled}
              placeholder="#3B82F6"
              className={cn(
                "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                error && "border-destructive focus-visible:ring-destructive"
              )}
              id={id}
              ref={ref}
              {...props}
            />
          )}
        </div>
        {renderModal()}
      </div>
      {description && !error && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
});

ColorPicker.displayName = "ColorPicker";

export default ColorPicker;